/**
 * Money is integer cents, everywhere, always.
 *
 * A billing system that stores dollars as floats produces a one-cent
 * discrepancy at exactly the moment someone is looking at a projector.
 */

/** Parse a spreadsheet price like "74.97" into 7497. Returns null for a value
 *  that is not a price — never 0, because "no price" and "free" are different
 *  facts and the 14 promo items depend on the distinction. */
export function parsePriceToCents(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).replace(/[$,\s]/g, "");
  if (text === "") return null;
  const value = Number(text);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

/* -------------------------------------------------------------------------- */
/* Typing money in                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Keep a money field to digits, one decimal point and two decimals.
 *
 * Applied on every keystroke, the way the minimum-level field strips
 * non-digits. `type="number"` was the alternative and it is worse on the phone
 * this app is built for: it accepts `1e5` and `-`, its spinners are a 12px tap
 * target, and what it hands back for a half-typed value is `""` — so the field
 * blanks itself under the thumb.
 *
 * Nine whole digits is the ceiling, comfortably past the `$10,000,000` cap in
 * `guards.cents` and short of where a float would start rounding.
 */
export function sanitiseDollarInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned.slice(0, 9);
  return `${cleaned.slice(0, dot).slice(0, 9)}.${cleaned
    .slice(dot + 1)
    .replace(/\./g, "")
    .slice(0, 2)}`;
}

/**
 * `"12.34"` → `1234`. The way in for money a human types.
 *
 * **No float arithmetic anywhere in here.** `Math.round(Number("12.34") * 100)`
 * is the obvious version and it is the bug this module's first paragraph is
 * about — the dollars and the cents are parsed as separate strings of digits
 * and combined as integers, so nothing is ever a fraction on the way through.
 *
 * `null` for anything that is not a price, so a caller has one thing to check:
 *
 *   - `""`, `"."`, `"abc"`, `"1.2.3"` — not a number.
 *   - `"12.345"` — **rejected, not truncated.** Silently dropping a digit off a
 *     figure somebody is typing into a billing system is how a cost goes in
 *     wrong and stays wrong; the field says so instead.
 *   - `"0"`, `"0.00"` — zero is not a cost. "No cost on file" is
 *     `pricePending`, a different fact, and the promo rows depend on it.
 *
 * `"12"` is 1200, `"12.3"` is 1230, `".5"` is 50 and `"12."` is 1200 — a
 * trailing point is somebody mid-keystroke, not a malformed price.
 */
export function parseDollarsToCents(raw: string): number | null {
  const text = raw.trim();
  if (!/\d/.test(text)) return null;
  if (!/^\d*(\.\d{0,2})?$/.test(text)) return null;

  const [whole, fraction = ""] = text.split(".");
  if (whole.length > 9) return null;

  const cents = Number(whole || "0") * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/** The one money format: `$1,234.56`, CAD, always two decimals.
 *
 *  No options. It carried a `currency` / `showCurrency` pair that all fifteen
 *  call sites ignored — a second currency is a data-model change (every stored
 *  figure is integer cents with no currency beside it), not a formatting flag,
 *  so the flag only ever offered a way for two screens to disagree.
 *
 *  One formatter, built once at module load. `Intl.NumberFormat` is expensive
 *  to construct and this is called seventy-odd places, several hundred times in
 *  a single invoice table render. */
const CAD = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatCents(cents: number): string {
  return CAD.format(cents / 100);
}

/** The bare amount: `1234.56`. No `$`, no thousands separator, two decimals.
 *
 *  Not a display format — the audit trail writes it, and audit rows are stored.
 *  Switching those strings to `formatCents` would leave the log in two formats
 *  either side of the change, so this stays byte-identical to what is already
 *  in the table. */
export function plainAmount(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Compact form for dashboard tiles: $8.6k. Full precision stays on invoices. */
export function formatCentsCompact(cents: number): string {
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 1_000_000) {
    return `$${(dollars / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(dollars) >= 1_000) return `$${(dollars / 1_000).toFixed(1)}k`;
  return `$${dollars.toFixed(0)}`;
}
